import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { KPI } from '../../../../@core/api';
import {
  ChartsService,
  I18nService,
  InvestmentsService,
  LayoutService,
} from '../../../../@core/utils';
import { BaseChartComponent } from '../base-chart/base-chart.component';
import { combineLatest, startWith, Subscription } from 'rxjs';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-budget-monitoring-chart',
  templateUrl: './budget-monitoring-chart.component.html',
  styleUrls: ['./budget-monitoring-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    
    HighchartsChartModule,
    NbAlertModule,
    NbButtonModule,
    TranslateModule
  ],
})
export class BudgetMonitoringChartComponent extends BaseChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public investmentIds: number[];
  @Input() budgetYear: number[];
  private chartTitle: string;
  private budgetReporting: KPI.BudgetReporting[];
  private subscriptions: Subscription[] = [];

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    protected chartsService: ChartsService,
    private i18nService: I18nService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private investmentsService: InvestmentsService
  ) {
    super(dialogService, layoutService, router);
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  public ngOnChanges(): void {
    this.chartTitle = this.translate.instant('Budget Monitoring');
    if (this.budgetYear?.length) {
      this.chartTitle += ' ' + this.budgetYear[0];
    }

    if (this.investmentIds?.length) {
      this.subscriptions.push(
        combineLatest([
          this.chartsService.getBudgetReportingByInvestment(this.investmentIds),
          this.investmentsService.reloadInvestment$.pipe(startWith())
        ])
        .subscribe(([budgetReporting, _]) => {
          this.budgetReporting = budgetReporting;
          this.update();
        })
      )
    }
  }

  protected update(): void {

    this.loading = true;

    const datas = this.budgetReporting.map(budget => budget.investment_budget.total_budget);

    const series = [
      {
        type: 'bar',
        showInLegend: false,
        data: datas
      },
    ]

    const categoriesName = this.budgetReporting.map(budget => budget.section.name);

    this.options = {
      ...this.getOptions(),
      // @ts-ignore
      xAxis: {
        ...this.getOptions().xAxis,
        categories: categoriesName,
        tickWidth: 0,
      },
      series: <Highcharts.SeriesCmfOptions[]>series,
    };
    this.loading = false;
    this.reflow();
    this.cdRef.detectChanges();
  }

  protected getOptions(): Highcharts.Options {
    const i18n = this.i18nService;

    return {
      ...super.getOptions(),
      colors: ['#9dc3e6', '#4e93d2', '#f88379'],
      title: {
        style: super.getOptions().title.style,
        text: this.chartTitle,
        align: 'left'
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          // @ts-ignore
          return '<b>' + this.x.name + ' : </b>' + i18n.formatCurrency(this.y);
        },
      },
      legend: {
        itemMarginTop: 5,
        itemMarginBottom: 5,
        itemHiddenStyle: super.getOptions().legend.itemHiddenStyle,
        verticalAlign: 'top',
        itemStyle: {
          fontSize: '0.9375rem',
          fontWeight: 'normal',
        },
      },
      plotOptions: {
        bar: {
          borderWidth: 0,
          cursor: 'pointer',
          colorByPoint: true,
          dataLabels: {
            style: {
              fontSize: '0.9375rem',
              color: '#7f7f7f',
              fontWeight: 'normal',
              textOutline: null,
            },
            enabled: true,
            crop: false,
            overflow: 'allow',
            formatter: function () {
              return i18n.formatCurrency(this.y);
            }
          },
        },
      },
      yAxis: {
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: {
          enabled: false,
        },
        title: {
          text: null,
        },
        maxPadding: 0.15,
        minPadding: 0.15,
      },
      xAxis: {
        labels: {
          style: {
            fontSize: '0.9375rem',
            color: '#091902',
          },
        },
        maxPadding: 0.15,
        minPadding: 0.15,
      },
      series: [],
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'bar',
      marginLeft: 175,
    };
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
